import { UpdateTimeOffTypePolicyRequest } from '../types/dto/request/time-off-policy-request';
import { TimeOffPolicyResponse } from '../types/dto/response/time-off-policy-response';
import {
  TimeOffTypeListItemResponse,
  TimeOffTypeResponse,
} from '../types/dto/response/time-off-type-response';
import {
  TimeOffAllocationFormat,
  TimeOffTypeCode,
  TimeOffTypeDetails,
  TimeOffTypeListItem,
} from '../types/time-off';
import { TimeOffTypeSetupFormData } from '../validations/time-off-type-validation';

const DEFAULT_RENEWAL_MONTH = 1;
const DEFAULT_RENEWAL_DAY = 1;

const TIME_OFF_TYPE_NAME_TO_CODE: Record<string, TimeOffTypeCode> = {
  'paid vacation': TimeOffTypeCode.PAID_VACATION,
  'public holiday': TimeOffTypeCode.PUBLIC_HOLIDAY,
  'sick leave': TimeOffTypeCode.SICK_LEAVE,
  'unpaid time off': TimeOffTypeCode.UNPAID_TIME_OFF,
  'unpaid time-off': TimeOffTypeCode.UNPAID_TIME_OFF,
};

const mapTimeOffTypeCodeFromName = (name: string): TimeOffTypeCode => {
  const normalizedName = name.trim().toLowerCase();

  return TIME_OFF_TYPE_NAME_TO_CODE[normalizedName] ?? TimeOffTypeCode.PAID_VACATION;
};

const getPolicyRenewalMonth = (policy: TimeOffPolicyResponse): number =>
  policy.renewalMonth ?? DEFAULT_RENEWAL_MONTH;

export const mapTimeOffTypeListItemFromResponse = (
  response: TimeOffTypeListItemResponse
): TimeOffTypeListItem => ({
  id: response.id,
  code: mapTimeOffTypeCodeFromName(response.name),
  displayName: response.name,
  format: response.allocationFormat ?? null,
  allocationDays: response.numberOfDays ?? null,
  dateOfRenewal: null,
  isApprovalRequired: null,
});

export const mapTimeOffTypeListFromResponse = (
  responses: TimeOffTypeListItemResponse[]
): TimeOffTypeListItem[] => responses.map(mapTimeOffTypeListItemFromResponse);

export const mapTimeOffTypeDetailsFromResponse = (
  response: TimeOffTypeResponse
): TimeOffTypeDetails => {
  const policy = response.activePolicy;

  return {
    id: response.id,
    code: mapTimeOffTypeCodeFromName(response.name),
    displayName: response.name,
    format: policy?.allocationFormat ?? TimeOffAllocationFormat.Yearly,
    allocationDays: policy?.numberOfDays ?? 0,
    dateOfRenewal: {
      month: policy ? getPolicyRenewalMonth(policy) : DEFAULT_RENEWAL_MONTH,
      day: policy?.renewalDay ?? DEFAULT_RENEWAL_DAY,
    },
    isApprovalRequired: policy?.approvalRequired ?? false,
    allowCarryover: policy?.allowCarryover ?? false,
    carryoverDays: policy?.allowCarryover ? policy.carryoverDays : null,
    allowNegativeBalance: policy?.allowNegativeBalance ?? false,
    negativeBalanceDays: policy?.allowNegativeBalance ? policy.negativeBalanceLimit : null,
    hasMaxLimit: policy?.maximumBalanceLimit != null,
    maxLimitDays: policy?.maximumBalanceLimit ?? null,
    requireFileUpload: policy?.requiresDocument ?? false,
    allowHalfDay: policy?.allowHalfDay ?? false,
    allowQuarterDay: policy?.allowQuarterDay ?? false,
    allowHourlyRequests: policy?.allowHourly ?? false,
    considerMemberCapacity: policy?.considerUserCapacity ?? false,
    isBillable: policy?.isBillable ?? false,
    creditOnCreation: policy?.creditOnCreation ?? false,
    prorateAccrual: policy?.prorateAccrual ?? true,
  };
};

/**
 * Maps form data to the scheduled-policy-change request body
 * (PUT /api/time-off/settings/types/{typeId}). `typeId` is a path parameter, so
 * it is not part of the body.
 */
export const mapTimeOffPolicyToUpdateRequest = ({
  data,
  effectiveStartDate,
  isBillable,
}: {
  data: TimeOffTypeSetupFormData;
  effectiveStartDate: string;
  isBillable: boolean;
}): UpdateTimeOffTypePolicyRequest => ({
  effectiveStartDate,
  allocationFormat: data.allocationFormat,
  renewalDay: data.dateOfRenewal.day,
  renewalMonth:
    data.allocationFormat === TimeOffAllocationFormat.Yearly ? data.dateOfRenewal.month : null,
  numberOfDays: data.allocationDays,
  allowCarryover: data.allowCarryover,
  carryoverDays: data.allowCarryover ? data.carryoverDays : null,
  maximumBalanceLimit: data.hasMaxLimit ? data.maxLimitDays : null,
  allowNegativeBalance: data.allowNegativeBalance,
  negativeBalanceLimit: data.allowNegativeBalance ? data.negativeBalanceDays : null,
  approvalRequired: data.isApprovalRequired,
  allowHalfDay: data.allowHalfDay,
  allowQuarterDay: data.allowQuarterDay,
  allowHourly: data.allowHourlyRequests,
  considerUserCapacity: data.considerMemberCapacity,
  requiresDocument: data.requireFileUpload,
  isBillable,
  creditOnCreation: data.creditOnCreation,
  prorateAccrual: data.prorateAccrual,
});
